import { Dialog } from '@headlessui/react';
import { Patient } from '../../../data/usePatients';
import React, { useMemo } from 'react';
import { useDigitalReceipt } from '../../../hooks/useDigitalReceipt';
import { Doctor } from '../../../types/doctor';
import Spinner from '../../Spinner';
import UploadAndSendPrescriptionButton from '../components/UploadAndSendPrescriptionButton';
import ModalInput from '../components/ModalInput';
import SendPaymentLinkButton from '../components/SendPaymentLinkButton';

interface DigitalReceiptModalProps {
  open: boolean;
  onClose: () => void;
  patient: Patient;
  doctor: Doctor;
  pharmacyId: number | null;
  appointmentId: number;
  handleRefetchAppointments: () => void;
  wasPaymentLinkSent: boolean;
}

const DigitalReceiptModal = ({
  open,
  onClose,
  patient,
  doctor,
  pharmacyId,
  appointmentId,
  handleRefetchAppointments,
  wasPaymentLinkSent,
}: DigitalReceiptModalProps) => {
  const receipt = useDigitalReceipt(patient, doctor, pharmacyId, appointmentId, open);

  const modalContent = useMemo(() => {
    if (!receipt.prescriptions) {
      return (
        <div className="flex items-center justify-center w-full h-[calc(100%-48px)]">
          <Spinner height="32px" width="32px" className="fill-swopa-secondary-light-blue" />
        </div>
      );
    }

    return (
      <>
        <h4 className="mb-2 font-bold text-blue-500">Patientendaten</h4>
        <div className="mb-2 grid grid-cols-12 gap-4">
          <ModalInput className="col-span-4" label="Nachname" value={receipt.patient.last_name} />
          <ModalInput className="col-span-4" label="Vorname" value={receipt.patient.first_name} />
          <ModalInput
            className="col-span-3"
            label="Geburstdatum"
            value={receipt.patient.date_of_birth}
          />
          <ModalInput
            className="col-span-6"
            label="Adresse"
            value={`${receipt.patient.street}, ${receipt.patient.postal_code}, ${receipt.patient.city}`}
          />
          <ModalInput
            className="col-span-2"
            label="Telefonnummer"
            value={receipt.patient.phone_number}
          />
          <ModalInput className="col-span-4" label="Email" value={receipt.patient.email} />
          <ModalInput
            className="col-span-4"
            label="Krankennumber"
            value={receipt.patient.health_insurance_number}
          />
        </div>
        <h4 className="mb-2 font-bold text-blue-500">Verschreibungen</h4>
        {receipt.prescriptions.map((p, i) => {
          let n = i + 1;
          return (
            <div key={n} className="mb-5">
              <h5 className="text-blue-500">Verschreibung {n}</h5>
              <div className="grid grid-cols-12 gap-4">
                <ModalInput className="col-span-6" label="Präparat" value={p.title} />
                <ModalInput className="col-span-3" label="Hersteller" value={p.producer} />
                <ModalInput className="col-span-3" label="Apothekencode" value={p.pharmacy_code} />
                <ModalInput className="col-span-2" label="Abgabeform" value={p.form} />
                <ModalInput className="col-span-2" label="Menge" value={p.amount} />
                <ModalInput className="col-span-2" label="Einheit" value={p.unit} />
                <ModalInput className="col-span-2" label="Anteil THC" value={p.share_of_thc} />
                <ModalInput className="col-span-2" label="Anteil CBD" value={p.share_of_cbd} />
                <ModalInput className="col-span-2" label="Dauer" value={p.duration} />
                <ModalInput
                  className="col-span-12"
                  label="Rezept Text"
                  value={p.prescription_text}
                />
              </div>
            </div>
          );
        })}
        <h4 className="mb-2 font-bold text-blue-500">Arztdaten</h4>
        <div className="mb-2 grid grid-cols-12 gap-4">
          <ModalInput className="col-span-4" label="Nachname" value={receipt.doctor.last_name} />
          <ModalInput className="col-span-4" label="Vorname" value={receipt.doctor.first_name} />
          <ModalInput className="col-span-4" label="GLN" value={receipt.doctor.gln_number} />
          <ModalInput
            className="col-span-6"
            label="Adresse"
            value={`${receipt.doctor.practice?.street}, ${receipt.doctor.practice?.postal_code}, ${receipt.doctor.practice?.city}`}
          />
          <ModalInput
            className="col-span-2"
            label="Telefonnummer"
            value={receipt.doctor.practice?.phone_number}
          />
          <ModalInput className="col-span-4" label="Email" value={receipt.doctor.practice?.email} />
          <ModalInput
            className="col-span-6"
            label="Apotheke"
            disabled={receipt.pharmacySelected}
            value={receipt.pharmacy}
          />
        </div>
        <div className="mb-2 grid grid-cols-12 gap-3">
          <div className="col-start-9 col-end-11">
            <SendPaymentLinkButton
              appointmentId={appointmentId}
              wasPaymentLinkSent={wasPaymentLinkSent}
              handleRefetchAppointments={handleRefetchAppointments}
            />
          </div>
          <div className="col-start-11 col-end-13">
            <UploadAndSendPrescriptionButton
              pharmacyId={pharmacyId}
              appointmentId={appointmentId}
              handleRefetchAppointments={handleRefetchAppointments}
              numberOfPrescriptions={receipt.prescriptions.length}
            />
          </div>
        </div>
      </>
    );
  }, [receipt.prescriptions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="fixed top-0 left-0 z-50 h-screen w-full filter backdrop-blur-md bg-swopa-secondary-light-blue/50"
    >
      <div className="flex h-full w-screen items-center justify-center p-4">
        <Dialog.Panel className="h-full w-full max-w-6xl overflow-auto rounded border bg-white p-4">
          <Dialog.Title>Digitales Rezept</Dialog.Title>
          {modalContent}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default DigitalReceiptModal;
